import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Field, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { pageContext } from "@/lib/activity";
import { toast } from "sonner";

export const CATEGORIES = [["how_to", "How-to"], ["bug", "Bug"], ["billing", "Billing"], ["data_issue", "Data issue"], ["integration", "Integration"], ["performance", "Performance"], ["access", "Access"], ["account_change", "Account details change"]];
export const SEVERITIES = ["low", "medium", "high", "critical"];

export function TicketDialog({ open, onClose, initial = {}, context, onCreated }) {
  const loc = useLocation();
  const [f, setF] = useState({ summary: initial.summary || "", description: initial.description || "", category: initial.category || "how_to", severity: initial.severity || "medium" });
  const ctx = context || pageContext(loc.pathname);
  const submit = async () => {
    try {
      const { data } = await api.post("/tickets", { ...f, context: ctx });
      toast.success(`Ticket ${data.number} created`);
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ticket-dialog">
        <DialogHeader><DialogTitle>Raise a support ticket</DialogTitle>
          <DialogDescription>{ctx.record_type ? `Linked to ${ctx.record_label || ctx.record_type}` : "Freeform ticket"} · page, module and recent actions are attached automatically.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Summary (1-2 lines)"><Input data-testid="ticket-summary-input" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} /></Field>
          <Field label="Description"><Textarea data-testid="ticket-description-input" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Category"><NativeSelect testId="ticket-category-select" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CATEGORIES.map(([value, label]) => ({ value, label }))} /></Field>
            <Field label="Severity"><NativeSelect testId="ticket-severity-select" value={f.severity} onChange={(v) => setF({ ...f, severity: v })} options={SEVERITIES.map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))} /></Field>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ticket-submit-button" disabled={f.summary.trim().length < 3} onClick={submit} className="bg-[#13294B] hover:bg-[#1c3a66]">Submit ticket</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RaiseTicketButton({ recordType, recordId, label, size }) {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size={size} data-testid={`raise-ticket-${recordType}${size ? `-${recordId}` : ""}`} onClick={() => setOpen(true)} className="print:hidden"><LifeBuoy className="mr-1 h-4 w-4" />Raise a ticket</Button>
      {open && <TicketDialog open onClose={() => setOpen(false)} context={pageContext(loc.pathname, { record_type: recordType, record_id: recordId, record_label: label })} />}
    </>
  );
}
